import { conversationContextSummary } from "@opencloud/agent-protocol";
import { ChatAttachments } from "./ChatAttachments.js";
import {
  AlertOctagon,
  AlertTriangle,
  Check,
  ChevronRight,
  CircleSlash,
  Copy,
  ExternalLink,
  ListChecks,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Bubble,
  BubbleContent,
  Button,
  Card,
  CardContent,
  Message as OpenCloudMessage,
  MessageContent,
  MessageHeader,
  ScrollArea,
} from "@opencloud/ui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { agentBrand } from "../brand.js";
import type {
  AgentRelease,
  ConversationOutcome,
  ChatSnapshot as ConversationSnapshot,
  IncidentView,
  OpenCloudUIMessage,
  PendingConversationMessage,
} from "../types.js";
import {
  latestActiveRoot,
  liveDeployment,
  presentedOutcomes,
  releaseHeadline,
  transcriptMessages,
} from "../presentation.js";
import { toolActivityIsLive } from "../tool-activity.js";
import { conversationTaskLists, isFinishedTaskList } from "../task-presentation.js";
import { openCloudUIMessagePartId } from "../ui-message-state.js";
import { AgentCardRenderer } from "./AgentCardRenderer.js";
import { Evidence } from "./Evidence.js";
import { Markdown } from "./Markdown.js";
import { TaskListRenderer } from "./TaskListRenderer.js";
import { ToolActivityCard } from "./ToolActivityCard.js";
import { AssignmentBriefs, assignmentRuns } from "./AssignmentBrief.js";
import { AutomationMessageCard, automationMessage } from "./AutomationMessageCard.js";
import { AppEventRow, knownAppEvent } from "./AppEventRow.js";
import { ContextSummaryNote } from "./ContextSummaryNote.js";
import { AutomaticFailureCard, type AutomaticFailureContext } from "./AutomaticFailureCard.js";
import { knownAutomaticFailure } from "../automatic-failures.js";

interface UIEntryIdentity {
  uiMessageId?: string;
  uiPartId?: string;
  uiPartType?: string;
  uiPartState?: string;
  uiSourceMessageId?: string;
}

type TimelineEntry = (
  | {
      kind: "message";
      at: string;
      sequence: number;
      value: ConversationSnapshot["messages"][number];
    }
  | {
      kind: "outcome";
      at: string;
      sequence: number;
      value: ConversationOutcome;
    }
  | {
      kind: "tool";
      at: string;
      sequence: number;
      value: NonNullable<ConversationSnapshot["toolCalls"]>[number];
    }
  | {
      kind: "pending-message";
      at: string;
      sequence: number;
      value: PendingConversationMessage;
    }
  | {
      kind: "incident-start";
      at: string;
      sequence: number;
      value: {
        incident: IncidentView;
        messageId: string | null;
      };
    }
  | {
      kind: "incident-terminal";
      at: string;
      sequence: number;
      value: {
        incident: IncidentView;
        messageId: string | null;
      };
    }
  | {
      kind: "reasoning";
      at: string;
      sequence: number;
      value: {
        text: string;
        state: "streaming" | "done";
      };
    }
  | {
      kind: "plan";
      at: string;
      sequence: number;
      value: Extract<
        OpenCloudUIMessage["parts"][number],
        { type: "data-plan" }
      >;
    }
  | {
      kind: "question";
      at: string;
      sequence: number;
      value: Extract<
        OpenCloudUIMessage["parts"][number],
        { type: "data-question" }
      >;
    }
  | {
      kind: "card";
      at: string;
      sequence: number;
      value: Extract<
        OpenCloudUIMessage["parts"][number],
        { type: "data-card" | "data-release" }
      >;
    }
) &
  UIEntryIdentity;

type ToolTimelineEntry = Extract<TimelineEntry, { kind: "tool" }>;
type TimelineGroup =
  | Exclude<TimelineEntry, ToolTimelineEntry>
  | { kind: "assignment"; at: string; sequence: number; runId: string }
  | { kind: "tools"; entries: ToolTimelineEntry[] };

export function Transcript({
  snapshot,
  viewerUserId = snapshot.participation?.actorUserId,
  incidents = [],
  releases,
  appUrl,
  attachedOutcomeRunId,
  onRetry,
  onLoadRelease,
  focusMessageId = null,
  pendingMessage = null,
  onRetryMessage = () => undefined,
  onAllowProvider,
  onConnectProvider,
  personalAiAccounts = true,
  onRetryAgentMessage = () => false,
  liveText = "",
  trailingContent = null,
  readOnly = false,
  now = new Date(),
  showAssignments = true,
  automaticFailures,
  onFixIncident,
}: {
  snapshot: ConversationSnapshot;
  viewerUserId?: string | undefined;
  incidents?: readonly IncidentView[];
  releases: readonly AgentRelease[];
  appUrl: string | null;
  attachedOutcomeRunId: string | null;
  onRetry: (runId: string) => void;
  onLoadRelease: (releaseId: string) => Promise<AgentRelease>;
  focusMessageId?: string | null;
  pendingMessage?: PendingConversationMessage | null;
  onRetryMessage?: () => void;
  onAllowProvider?: (() => void) | undefined;
  onConnectProvider?: (() => void) | undefined;
  /** False where only organisation admins connect AI accounts. */
  personalAiAccounts?: boolean;
  onRetryAgentMessage?: (messageId: string) => boolean | Promise<boolean>;
  liveText?: string;
  trailingContent?: ReactNode;
  readOnly?: boolean;
  now?: Date;
  showAssignments?: boolean;
  /** The app's automatic choice and actions for automatic failure cards. */
  automaticFailures?: AutomaticFailureContext | undefined;
  /** Asks the app's Agent to fix an alert it didn't fix by itself. */
  onFixIncident?: ((incident: IncidentView) => Promise<void>) | undefined;
}) {
  const sourceSnapshot = snapshot;
  const sourceIncidents = incidents;
  const showBriefs = showAssignments && !readOnly && snapshot.participation?.role !== "builder";
  if (showBriefs) {
    const represented = new Set(assignmentRuns(snapshot).map(run => run.id));
    const incidentMessageIds = new Set(snapshot.messages.filter(message => incidents.some(incident =>
      incident.runId && represented.has(incident.runId) && incidentLedgerStage(message, incident) !== null)).map(message => message.id));
    snapshot = { ...snapshot,
      messages: snapshot.messages.filter(message => !incidentMessageIds.has(message.id)
        && (message.role === "user" || !message.runId || !represented.has(message.runId))),
      ...(snapshot.uiMessages ? { uiMessages: snapshot.uiMessages.filter(message => !incidentMessageIds.has(message.id)
        && (message.role === "user" || !message.metadata.runId || !represented.has(message.metadata.runId))) } : {}),
      ...(snapshot.outcomes ? { outcomes: snapshot.outcomes.filter(outcome => !represented.has(outcome.runId)) } : {}),
      ...(snapshot.taskLists ? { taskLists: snapshot.taskLists.filter(plan => !represented.has(plan.runId)) } : {}),
    };
    incidents = incidents.flatMap(incident => !incident.runId || !represented.has(incident.runId) ? [incident]
      // A repair that failed or stopped keeps its brief; its card offers to try again.
      : incidentNeedsPerson(incident) ? [{ ...incident, runId: null }] : []);
  }
  const frame = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const lastFocusedMessage = useRef<string | null>(null);
  const entries = useMemo<TimelineGroup[]>(
    () => {
      const timeline = snapshot.uiMessages === undefined
        ? legacyTimelineEntries({
            snapshot,
            incidents,
            releases,
            attachedOutcomeRunId,
            pendingMessage,
          })
        : typedTimelineEntries({
            snapshot,
            incidents,
            releases,
            attachedOutcomeRunId,
            pendingMessage,
            readOnly,
          });
      if (!showBriefs) return timeline;
      const combined: TimelineGroup[] = [...timeline, ...assignmentRuns(sourceSnapshot).map(run => ({
        kind: "assignment" as const, at: run.createdAt, sequence: 0, runId: run.id,
      }))];
      return combined.sort((left, right) => (left.kind === "tools" ? left.entries[0]!.at : left.at)
        .localeCompare(right.kind === "tools" ? right.entries[0]!.at : right.at));
    },
    [attachedOutcomeRunId, incidents, pendingMessage, readOnly, releases, snapshot, showBriefs, sourceSnapshot],
  );

  useEffect(() => {
    if (!following.current) return;
    const element = transcriptViewport(frame.current);
    if (element) element.scrollTop = element.scrollHeight;
  }, [entries, trailingContent]);

  useEffect(() => {
    const element = transcriptViewport(frame.current);
    if (!element) return;
    const trackPosition = () => {
      following.current =
        element.scrollHeight - element.scrollTop - element.clientHeight < 96;
    };
    element.addEventListener("scroll", trackPosition, { passive: true });
    return () => element.removeEventListener("scroll", trackPosition);
  }, []);

  useEffect(() => {
    if (!focusMessageId) {
      lastFocusedMessage.current = null;
      return;
    }
    if (lastFocusedMessage.current === focusMessageId) return;
    const element = transcriptViewport(frame.current);
    const target = Array.from(
      element?.querySelectorAll<HTMLElement>("[data-message-id]") ?? [],
    ).find((candidate) => candidate.dataset.messageId === focusMessageId);
    if (!element || !target) return;
    element.scrollTo({
      top: Math.max(0, target.offsetTop - element.clientHeight * 0.24),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
    lastFocusedMessage.current = focusMessageId;
    following.current = false;
  }, [entries, focusMessageId]);
  const startLabel = conversationStartLabel(snapshot.thread.createdAt, now);

  return (
    <div className="a3-transcript-frame" ref={frame}>
      <ScrollArea className="a3-transcript">
        <div className="a3-transcript-content">
          {entries.length === 0 ? (
            <div className="a3-empty-transcript">
              <h2>What should we build?</h2>
              <p>
                Describe who it is for, what they need to do, and anything the
                app should remember. A short paragraph is enough.
              </p>
            </div>
          ) : (
            <>
              {startLabel ? (
                <time
                  className="a3-conversation-start a3-day-marker"
                  dateTime={snapshot.thread.createdAt}
                >
                  {startLabel}
                </time>
              ) : null}
              {entries.map((entry, index) =>
                entry.kind === "assignment" ? <AssignmentBriefs key={`assignment:${entry.runId}`} snapshot={sourceSnapshot}
                  incidents={sourceIncidents} onlyRunId={entry.runId} /> : entry.kind === "tools" ? (
                  <ToolGroup
                    key={`tools:${entry.entries[0]!.value.id}`}
                    entries={entry.entries}
                    runs={snapshot.runs}
                    {...(!readOnly && !entries.slice(index + 1).some((candidate) => candidate.kind === "tools") ? { onRetry } : {})}
                  />
                ) : entry.kind === "message" ? (
                  <UIMessagePartFrame
                    key={`message:${entry.value.id}:${entry.uiPartId ?? "legacy"}`}
                    identity={entry}
                  >
                    <TranscriptMessage
                      viewerUserId={viewerUserId}
                      message={entry.value}
                      focused={entry.value.id === focusMessageId}
                      readOnly={readOnly}
                      onRetry={onRetryAgentMessage}
                      streaming={entry.uiPartState === "streaming"}
                      canRegenerate={
                        entry.uiMessageId === undefined ||
                        entry.uiSourceMessageId !== undefined
                      }
                      automaticRepair={incidents.some(
                        (incident) =>
                          incident.runId !== null &&
                          incident.runId === entry.value.runId,
                      )}
                      automaticFailures={automaticFailures}
                    />
                  </UIMessagePartFrame>
                ) : entry.kind === "pending-message" ? (
                  <PendingMessage
                    key={entry.value.id}
                    message={entry.value}
                    onRetry={onRetryMessage}
                    onAllowProvider={onAllowProvider}
                    onConnectProvider={onConnectProvider}
                    personalAiAccounts={personalAiAccounts}
                  />
                ) : entry.kind === "incident-start" ||
                  entry.kind === "incident-terminal" ? (
                  <IncidentLifecycleCard
                    key={`${entry.kind}:${entry.value.incident.id}`}
                    incident={entry.value.incident}
                    stage={
                      entry.kind === "incident-start" ? "start" : "terminal"
                    }
                    messageId={entry.value.messageId}
                    focused={entry.value.messageId === focusMessageId}
                    identity={entry}
                    onFix={readOnly ? undefined : onFixIncident}
                  />
                ) : entry.kind === "reasoning" ? (
                  <UIMessagePartFrame
                    key={`reasoning:${entry.uiPartId}`}
                    identity={entry}
                  >
                    <ReasoningSummary
                      text={entry.value.text}
                      streaming={entry.value.state === "streaming"}
                    />
                  </UIMessagePartFrame>
                ) : entry.kind === "plan" ? (
                  <UIMessagePartFrame
                    key={`plan:${entry.value.id}`}
                    identity={entry}
                  >
                    <PlanPart part={entry.value} />
                  </UIMessagePartFrame>
                ) : entry.kind === "question" ? (
                  <UIMessagePartFrame
                    key={`question:${entry.value.id}`}
                    identity={entry}
                  >
                    <QuestionPart part={entry.value} />
                  </UIMessagePartFrame>
                ) : entry.kind === "card" ? (
                  <UIMessagePartFrame
                    key={`card:${entry.value.id}`}
                    identity={entry}
                  >
                    <AgentCardRenderer card={entry.value.data.card} />
                  </UIMessagePartFrame>
                ) : (
                  <UIMessagePartFrame
                    key={`outcome:${entry.value.runId}:${entry.value.occurredAt}`}
                    identity={entry}
                  >
                    <OutcomeCard
                      outcome={entry.value}
                      run={
                        snapshot.runs.find(
                          ({ id }) => id === entry.value.runId,
                        ) ?? null
                      }
                      releases={releases}
                      appName={snapshot.app?.name ?? null}
                      repeatSummary={snapshot.messages.some((message) => message.role === "agent" && message.runId === entry.value.runId && message.content.trim() === entry.value.summary.trim())}
                      appUrl={appUrl}
                      onLoadRelease={onLoadRelease}
                      readOnly={readOnly}
                    />
                  </UIMessagePartFrame>
                ),
              )}
            </>
          )}
          {snapshot.uiMessages === undefined && liveText.trim() ? (
            <StreamingMessage text={liveText} />
          ) : null}
          {trailingContent}
        </div>
      </ScrollArea>
    </div>
  );
}

interface TimelineInput {
  snapshot: ConversationSnapshot;
  incidents: readonly IncidentView[];
  releases: readonly AgentRelease[];
  attachedOutcomeRunId: string | null;
  pendingMessage: PendingConversationMessage | null;
  readOnly?: boolean;
}

function legacyTimelineEntries({
  snapshot,
  incidents,
  releases,
  attachedOutcomeRunId,
  pendingMessage,
}: TimelineInput): TimelineGroup[] {
  const transcript = transcriptMessages(snapshot.messages, snapshot.runs);
  const threadIncidents = conversationIncidents(
    snapshot,
    incidents,
    transcript,
  );
  const messages: TimelineEntry[] = transcript
    .filter(
      (message) =>
        !threadIncidents.some((incident) =>
          isIncidentLedgerMessage(message, incident),
        ),
    )
    .map((value) => ({
      kind: "message",
      at: value.createdAt,
      sequence: value.sequence,
      value,
    }));
  const incidentEntries = threadIncidents.flatMap((incident) =>
    incidentTimelineEntries(incident, transcript),
  );
  const outcomes: TimelineEntry[] = presentedOutcomes(
    snapshot.outcomes ?? [],
    snapshot.runs,
    releases,
  )
    .filter((value) => value.runId !== attachedOutcomeRunId)
    .filter((value) =>
      value.status === "released" || value.status === "completed"
    )
    .map((value, index) => ({
      kind: "outcome",
      at: value.occurredAt,
      sequence: Number.MAX_SAFE_INTEGER - 100 + index,
      value,
    }));
  const tools: TimelineEntry[] = (snapshot.toolCalls ?? []).map((value) => ({
    kind: "tool",
    at: value.startedAt,
    sequence: value.sequence,
    value,
  }));
  const plans: TimelineEntry[] = conversationTaskLists(snapshot).flatMap((record) => {
    const run = snapshot.runs.find((candidate) => candidate.id === record.runId);
    return isFinishedTaskList(record.taskList, run) ? [{
      kind: "plan" as const, at: record.updatedAt, sequence: Number.MAX_SAFE_INTEGER - 150,
      value: { type: "data-plan" as const, id: `plan:${record.runId}:${record.taskList.id}`,
        data: { ...record, state: "done" as const } },
    }] : [];
  });
  return settleTimeline(deliveryEntries([
    ...messages,
    ...incidentEntries,
    ...outcomes,
    ...tools,
    ...plans,
    ...pendingTimelineEntry(snapshot, pendingMessage),
  ], snapshot, releases));
}

function typedTimelineEntries({
  snapshot,
  incidents,
  releases,
  attachedOutcomeRunId,
  pendingMessage,
  readOnly = false,
}: TimelineInput): TimelineGroup[] {
  const messages = snapshot.uiMessages ?? [];
  const visibleTranscript = transcriptMessages(
    snapshot.messages,
    snapshot.runs,
  );
  const visibleById = new Map(
    visibleTranscript.map((message) => [message.id, message]),
  );
  const threadIncidents = conversationIncidents(
    snapshot,
    incidents,
    visibleTranscript,
  );
  const activeRootId = latestActiveRoot(snapshot.runs)?.id ?? null;
  const plansByRun = new Map(conversationTaskLists(snapshot).map((record) => [record.runId, record]));
  const renderedPlans = new Set<string>();
  const terminalRunIds = new Set(
    messages.flatMap(({ parts }) =>
      parts.flatMap((part) =>
        part.type === "data-terminal" ? [part.data.runId] : [],
      ),
    ),
  );
  const entries: TimelineEntry[] = [];

  for (const message of messages) {
    const sourceMessage = message.metadata.sourceMessageId
      ? visibleById.get(message.metadata.sourceMessageId)
      : undefined;
    if (message.metadata.source === "transcript" && !sourceMessage) continue;
    if (
      sourceMessage &&
      threadIncidents.some((incident) =>
        isIncidentLedgerMessage(sourceMessage, incident),
      )
    ) {
      continue;
    }

    for (const [partIndex, part] of message.parts.entries()) {
      const identity: UIEntryIdentity = {
        uiMessageId: message.id,
        uiPartId: openCloudUIMessagePartId(message.id, part),
        uiPartType: part.type,
        uiPartState: uiPartState(part),
        ...(message.metadata.sourceMessageId
          ? { uiSourceMessageId: message.metadata.sourceMessageId }
          : {}),
      };
      const fallbackSequence =
        (message.metadata.sequence ?? Number.MAX_SAFE_INTEGER - 10_000) +
        partIndex;

      if (part.type === "data-attachment" && sourceMessage && !sourceMessage.content) {
        if (partIndex === 0) entries.push({kind:"message",at:message.metadata.createdAt,
          sequence:fallbackSequence,value:sourceMessage,...identity});
        continue;
      }
      if (part.type === "text") {
        const value = sourceMessage
          ? { ...sourceMessage, content: part.text }
          : {
              id: message.id,
              ...("actor" in message.metadata && message.metadata.actor ? { actor: message.metadata.actor as NonNullable<ConversationSnapshot["messages"][number]["actor"]> } : {}),
              threadId: message.metadata.threadId,
              appId: snapshot.app?.id ?? null,
              runId: message.metadata.runId,
              role:
                message.role === "assistant"
                  ? ("agent" as const)
                  : message.role,
              ...(message.metadata.presentation
                ? { presentation: message.metadata.presentation }
                : {}),
              content: part.text,
              createdAt: message.metadata.createdAt,
              sequence: fallbackSequence,
            };
        entries.push({
          kind: "message",
          at: message.metadata.createdAt,
          sequence: fallbackSequence,
          value,
          ...identity,
        });
        continue;
      }

      if (part.type === "reasoning") {
        entries.push({
          kind: "reasoning",
          at: message.metadata.createdAt,
          sequence: fallbackSequence,
          value: {
            text: part.text,
            state: part.state ?? "done",
          },
          ...identity,
        });
        continue;
      }

      if (part.type === "dynamic-tool") {
        const status = part.toolMetadata.status;
        entries.push({
          kind: "tool",
          at: part.toolMetadata.startedAt ?? message.metadata.createdAt,
          sequence: part.toolMetadata.sequence,
          value: {
            id: part.toolCallId,
            runId: part.toolMetadata.runId,
            tool: part.toolName,
            kind: part.toolMetadata.kind,
            summary: part.title,
            input: part.input,
            output:
              part.state === "output-available"
                ? part.output
                : part.state === "output-error"
                  ? part.errorText
                  : null,
            status,
            errorClass: status === "failed" ? "tool_error" : null,
            durationMs: part.toolMetadata.durationMs,
            startedAt:
              part.toolMetadata.startedAt ?? message.metadata.createdAt,
            finishedAt: part.toolMetadata.finishedAt,
            sequence: part.toolMetadata.sequence,
          },
          ...identity,
        });
        continue;
      }

      if (part.type === "data-plan") {
        const plan = plansByRun.get(part.data.runId) ?? part.data;
        if (
          part.data.runId === activeRootId ||
          renderedPlans.has(part.data.runId) ||
          !isFinishedTaskList(plan.taskList, snapshot.runs.find((run) => run.id === part.data.runId))
        ) {
          continue;
        }
        renderedPlans.add(part.data.runId);
        entries.push({
          kind: "plan",
          at: plan.updatedAt,
          sequence: fallbackSequence,
          value: { ...part, data: { ...part.data, ...plan, state: "done" } },
          ...identity,
        });
        continue;
      }

      if (part.type === "data-question") {
        if (!readOnly && part.data.questionId === snapshot.question?.questionId) continue;
        entries.push({
          kind: "question",
          at: part.data.occurredAt,
          sequence: fallbackSequence,
          value: part,
          ...identity,
        });
        continue;
      }

      // Sub-agent activity is available from the composer list.
      if (part.type === "data-child") continue;

      if (part.type === "data-card") {
        if (part.data.card.type === "work_card") continue;
        if (
          !readOnly && (part.data.runId === activeRootId ||
          part.data.runId === attachedOutcomeRunId)
        ) {
          continue;
        }
        entries.push({
          kind: "card",
          at: part.data.occurredAt,
          sequence: fallbackSequence,
          value: part,
          ...identity,
        });
        continue;
      }

      if (part.type === "data-release") {
        if (
          terminalRunIds.has(part.data.runId) ||
          part.data.runId === attachedOutcomeRunId
        ) {
          continue;
        }
        entries.push({
          kind: "card",
          at: part.data.occurredAt,
          sequence: fallbackSequence,
          value: part,
          ...identity,
        });
        continue;
      }

      if (part.type === "data-terminal") {
        if (part.data.runId === attachedOutcomeRunId) continue;
        const value =
          presentedOutcomes([part.data], snapshot.runs, releases)[0] ??
          part.data;
        if (value.status === "failed" || value.status === "cancelled")
          continue;
        entries.push({
          kind: "outcome",
          at: part.data.occurredAt,
          sequence: fallbackSequence,
          value,
          ...identity,
        });
      }
      // Incident parts are composed below from the separately authorized,
      // durable incident endpoint. A snapshot must never smuggle a second or
      // richer incident copy into the transcript.
    }
  }

  for (const incident of threadIncidents) {
    entries.push(...incidentTimelineEntries(incident, visibleTranscript, true));
  }
  entries.push(...pendingTimelineEntry(snapshot, pendingMessage));
  return settleTimeline(deliveryEntries(entries, snapshot, releases));
}

/** Add a handoff from exact deployment ownership, even if the run loses its final reply. */
function deliveryEntries(entries: TimelineEntry[], snapshot: ConversationSnapshot, releases: readonly AgentRelease[]): TimelineEntry[] {
  const active = liveDeployment(snapshot);
  const runId = active?.activatedByAgentRootRunId;
  if (active && runId && active.activatedAt && snapshot.runs.some((run) => run.id === runId && run.parentRunId === null)
    && !entries.some((entry) => entry.kind === "outcome" && entry.value.runId === runId && entry.value.release?.deploymentId === active.id)) {
    const retained = releases.find((release) => release.id === active.id);
    const completion = entries.find((entry) => entry.kind === "outcome" && entry.value.runId === runId && entry.value.status === "completed");
    entries.push({ kind: "outcome", at: active.activatedAt, sequence: Number.MAX_SAFE_INTEGER - 90,
      value: { runId, status: "released", occurredAt: active.activatedAt,
        summary: completion?.kind === "outcome" ? completion.value.summary : retained?.summary || `${snapshot.app?.name ?? "Your app"} was published successfully.`,
        release: { deploymentId: active.id, status: retained?.gates.some((gate) => gate.status === "failed") ? "failed" : "passed",
          reviews: retained?.reviews ?? [], gates: retained?.gates ?? [] } } });
  }
  const releaseRuns = new Set(entries.flatMap((entry) => entry.kind === "outcome" && entry.value.status === "released" && entry.value.release?.deploymentId ? [entry.value.runId] : []));
  const releasesSeen = new Set<string>();
  return entries.filter((entry) => {
    if (entry.kind === "card" && entry.value.type === "data-release" && releaseRuns.has(entry.value.data.runId)) return false;
    if (entry.kind !== "outcome") return true;
    if (entry.value.status === "completed" && entries.some((candidate) => candidate.kind === "outcome"
      && candidate.value.status === "released" && candidate.value.runId === entry.value.runId
      && candidate.value.summary.trim() === entry.value.summary.trim())) return false;
    if (entry.value.status !== "released" || !entry.value.release?.deploymentId) return true;
    const identity = `${entry.value.runId}:${entry.value.release.deploymentId}`;
    if (releasesSeen.has(identity)) return false;
    releasesSeen.add(identity);
    return true;
  });
}

function conversationIncidents(
  snapshot: ConversationSnapshot,
  incidents: readonly IncidentView[],
  transcript: ConversationSnapshot["messages"],
): IncidentView[] {
  return incidents.filter(
    (incident) =>
      incident.threadId === snapshot.thread.id ||
      // An alert no repair has picked up waits in the app's chat, even one
      // that arrived before the chat existed.
      (incident.threadId === null && incidentNeedsPerson(incident)) ||
      transcript.some((message) => isIncidentLedgerMessage(message, incident)),
  );
}

/** Alerts the app's Agent didn't fix by itself; someone can ask it to. */
export function incidentNeedsPerson(incident: IncidentView): boolean {
  return ["awaiting_user", "suppressed", "failed", "cancelled"].includes(incident.status);
}

/**
 * Alerts for an app whose chat doesn't exist yet, shown where the chat will
 * start. Asking for the fix creates the chat.
 */
export function WaitingIncidents({
  incidents,
  onFix,
}: {
  incidents: readonly IncidentView[];
  onFix?: ((incident: IncidentView) => Promise<void>) | undefined;
}) {
  const waiting = incidents.filter((incident) => incident.threadId === null && incidentNeedsPerson(incident));
  if (!waiting.length) return null;
  return (
    <>
      {waiting.map((incident) => (
        <IncidentLifecycleCard
          key={incident.id}
          incident={incident}
          stage="terminal"
          messageId={null}
          focused={false}
          identity={{}}
          onFix={onFix}
        />
      ))}
    </>
  );
}

function pendingTimelineEntry(
  snapshot: ConversationSnapshot,
  pendingMessage: PendingConversationMessage | null,
): TimelineEntry[] {
  if (
    !pendingMessage ||
    snapshot.messages.some(
      (message) =>
        message.role === "user" &&
        (!message.actor || !snapshot.participation || message.actor.userId === snapshot.participation.actorUserId) &&
        message.createdAt === pendingMessage.sentAt &&
        message.content === pendingMessage.content,
    )
  ) {
    return [];
  }
  return [
    {
      kind: "pending-message",
      at: pendingMessage.sentAt,
      sequence: Number.MAX_SAFE_INTEGER,
      value: pendingMessage,
    },
  ];
}

function settleTimeline(entries: TimelineEntry[]): TimelineGroup[] {
  const sorted = entries.sort(
      (left, right) =>
        left.at.localeCompare(right.at) ||
        left.sequence - right.sequence ||
        (left.uiPartId ?? "").localeCompare(right.uiPartId ?? ""),
    );
  return groupToolEntries(sorted.filter((entry, index) => {
    if (entry.kind !== "message" || entry.value.role !== "system") return true;
    return ![sorted[index - 1], sorted[index + 1]].some((neighbor) =>
      neighbor?.kind === "outcome" && neighbor.value.status === "released" && neighbor.value.release?.deploymentId
      && neighbor.value.runId === entry.value.runId && neighbor.value.summary.trim() === entry.value.content.trim());
  }));
}

function uiPartState(part: OpenCloudUIMessage["parts"][number]): string {
  if (part.type === "data-attachment") return "done";
  if (part.type === "text" || part.type === "reasoning") {
    return part.state ?? "done";
  }
  if (part.type === "dynamic-tool") return part.state;
  if (part.type === "data-plan") return part.data.state;
  if (part.type === "data-question") return part.data.state;
  if (part.type === "data-child") return part.data.state;
  if (part.type === "data-card" || part.type === "data-release") {
    return part.data.card.state;
  }
  if (part.type === "data-incident") return part.data.status;
  return part.data.status;
}

function UIMessagePartFrame({
  identity,
  children,
}: {
  identity: UIEntryIdentity;
  children: ReactNode;
}) {
  if (!identity.uiMessageId) return <>{children}</>;
  return (
    <div
      className="a3-ui-message-part"
      data-ui-message-id={identity.uiMessageId}
      data-ui-part-id={identity.uiPartId}
      data-ui-part-type={identity.uiPartType}
      data-ui-part-state={identity.uiPartState}
    >
      {children}
    </div>
  );
}

function ReasoningSummary({
  text,
  streaming,
}: {
  text: string;
  streaming: boolean;
}) {
  return (
    <Accordion
      className="a3-reasoning-summary"
      aria-label="Agent reasoning summary"
      data-state={streaming ? "streaming" : "done"}
    >
      <AccordionItem value="reasoning">
        <AccordionTrigger>
          <span className="a3-reasoning-heading">
            <Sparkles aria-hidden="true" />
            <span>
              <strong>Reasoning summary</strong>
              <small>{streaming ? "Updating…" : "Shared by the agent"}</small>
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <Markdown>{text}</Markdown>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function PlanPart({
  part,
}: {
  part: Extract<OpenCloudUIMessage["parts"][number], { type: "data-plan" }>;
}) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <TaskListRenderer
      taskList={part.data.taskList}
      lifecycle="completed"
      collapsible
      collapsed={collapsed}
      onToggleCollapse={() => setCollapsed((value) => !value)}
    />
  );
}

function QuestionPart({
  part,
}: {
  part: Extract<OpenCloudUIMessage["parts"][number], { type: "data-question" }>;
}) {
  const approval = part.data.kind === "approval";
  const closed = part.data.state === "closed";
  return (
    <Card
      className="a3-question-history"
      data-question-kind={part.data.kind}
      data-state={part.data.state}
      aria-label={`${approval ? "Approval" : "Question"}: ${part.data.question}`}
    >
      <CardContent>
        <p className="a3-eyebrow">
          {approval ? "Approval requested" : "Question asked"}
          {closed ? " · Closed" : null}
        </p>
        <h3>{part.data.question}</h3>
        {part.data.context ? <Markdown>{part.data.context}</Markdown> : null}
        {part.data.options.length > 0 ? (
          <ul aria-label="Options the agent presented">
            {part.data.options.map((option) => (
              <li key={option.id}>
                <strong>{option.label}</strong>
                <span>{option.detail}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

function incidentTimelineEntries(
  incident: IncidentView,
  messages: ConversationSnapshot["messages"],
  typed = false,
): Extract<TimelineEntry, { kind: "incident-start" | "incident-terminal" }>[] {
  // A repair someone asked for after an earlier try is placed at its own time.
  const latest = [...messages].reverse();
  const startMessage = latest.find(
    (message) => incidentLedgerStage(message, incident) === "start",
  );
  const terminalMessage = latest.find(
    (message) => incidentLedgerStage(message, incident) === "terminal",
  );
  const terminal = [
    "awaiting_user",
    "suppressed",
    "repaired",
    "failed",
    "cancelled",
  ].includes(incident.status);
  const entries: Extract<
    TimelineEntry,
    { kind: "incident-start" | "incident-terminal" }
  >[] = [];

  if (startMessage || incident.runId || !terminal) {
    entries.push({
      kind: "incident-start",
      at: startMessage?.createdAt ?? incident.firstObservedAt,
      sequence: startMessage?.sequence ?? Number.MAX_SAFE_INTEGER - 300,
      value: {
        incident,
        messageId: startMessage?.id ?? null,
      },
      ...(typed
        ? {
            uiMessageId: `incident:${incident.id}:start`,
            uiPartId: `incident:${incident.id}:start`,
            uiPartType: "data-incident",
            uiPartState:
              incident.status === "detected" || incident.status === "queued"
                ? incident.status
                : "running",
          }
        : {}),
    });
  }
  if (terminal) {
    entries.push({
      kind: "incident-terminal",
      at:
        terminalMessage?.createdAt ??
        incident.resolvedAt ??
        incident.lastObservedAt,
      sequence: terminalMessage?.sequence ?? Number.MAX_SAFE_INTEGER - 50,
      value: {
        incident,
        messageId: terminalMessage?.id ?? null,
      },
      ...(typed
        ? {
            uiMessageId: `incident:${incident.id}:terminal`,
            uiPartId: `incident:${incident.id}:terminal`,
            uiPartType: "data-incident",
            uiPartState: incident.status,
          }
        : {}),
    });
  }
  return entries;
}

function isIncidentLedgerMessage(
  message: ConversationSnapshot["messages"][number],
  incident: IncidentView,
): boolean {
  return incidentLedgerStage(message, incident) !== null;
}

function incidentLedgerStage(
  message: ConversationSnapshot["messages"][number],
  incident: IncidentView,
): "start" | "terminal" | null {
  if (message.role !== "system") return null;
  // Stored ledger wording is a fixed format under every brand; the lifecycle
  // cards show branded copy instead of these messages.
  if (
    message.content.startsWith(
      "OpenCloud detected a trusted app-scoped incident.",
    ) &&
    message.content.includes(`\nIncident: ${incident.id}\n`)
  ) {
    return "start";
  }
  if (
    message.content.startsWith(
      `OpenCloud's app agent completed repair ${incident.id}.`,
    ) ||
    message.content.startsWith(`Automatic repair ${incident.id} `) ||
    message.content.startsWith(`OpenCloud detected incident ${incident.id} (`)
  ) {
    return "terminal";
  }
  return null;
}

function IncidentLifecycleCard({
  incident,
  stage,
  messageId,
  focused,
  identity,
  onFix,
}: {
  incident: IncidentView;
  stage: "start" | "terminal";
  messageId: string | null;
  focused: boolean;
  identity: UIEntryIdentity;
  onFix?: ((incident: IncidentView) => Promise<void>) | undefined;
}) {
  const [fixing, setFixing] = useState(false);
  const resolved = stage === "terminal" && incident.status === "repaired";
  const attention = stage === "terminal" && !resolved;
  const state = resolved ? "resolved" : attention ? "attention" : "in-progress";
  const status = resolved
    ? "Resolved"
    : attention
      ? terminalIncidentStatus(incident.status)
      : incident.status === "detected" || incident.status === "queued"
        ? "Unresolved"
        // The start card stays in the history once the alert is closed.
        : incident.status === "running" ? "In progress" : "Started";
  const title = resolved ? recoveryTitle(incident.title) : incident.title;
  const summary = resolved
    ? "The repaired release passed its verification checks, and this alert fire is now closed."
    : incident.summary;
  const action = resolved
    ? {
        title: "Recovery verified",
        detail:
          "The repaired release passed its checks and the alert fire was closed.",
      }
    : attention
      ? {
          title: terminalIncidentAction(incident.status),
          detail:
            incident.suppressionReason ??
            "It stays open until a fix is released and checked.",
        }
      : startIncidentAction(incident);
  const fixable = attention && onFix !== undefined && incidentNeedsPerson(incident);
  const label = incidentLabel(incident.source);
  const facts = resolved
    ? [
        {
          label: "Time to recover",
          value: recoveryDuration(
            incident.firstObservedAt,
            incident.resolvedAt,
          ),
        },
        {
          label: "Occurrences",
          value: String(incident.occurrenceCount),
        },
        {
          label: "Evidence",
          value: retainedEvidenceSummary(incident.repairEvidence),
        },
      ]
    : attention
      ? [
          {
            label: "Occurrences",
            value: String(incident.occurrenceCount),
          },
          {
            label: "Observed",
            value: formatClockTime(incident.lastObservedAt),
          },
        ]
      : [
          {
            label: "Occurrences",
            value: String(incident.occurrenceCount),
          },
          { label: "Severity", value: titleCase(incident.severity) },
          {
            label: "Started",
            value: formatClockTime(incident.firstObservedAt),
          },
        ];
  const details = resolved
    ? [
        { label: "Result", value: "Verified repair released" },
        {
          label: "Evidence retained",
          value: retainedEvidenceKinds(incident.repairEvidence),
        },
        {
          label: "Resolved",
          value: formatDateTime(incident.resolvedAt ?? incident.lastObservedAt),
        },
        { label: "Delivery", value: "Closed once after verified recovery" },
      ]
    : [
        { label: "Source", value: sourceLabel(incident.source) },
        { label: "Signal", value: titleCase(incident.kind) },
        { label: "Policy", value: incident.policy.reason },
        {
          label: "Delivery",
          value: incident.runId
            ? "Accepted once by the app agent"
            : "Recorded for review",
        },
      ];

  return (
    <Card
      id={messageId ? `message-${messageId}` : undefined}
      data-message-id={messageId ?? undefined}
      data-search-focus={focused || undefined}
      data-ui-message-id={identity.uiMessageId}
      data-ui-part-id={identity.uiPartId}
      data-ui-part-type={identity.uiPartType}
      data-ui-part-state={identity.uiPartState}
      className="a3-automation-event"
      data-state={state}
      aria-label={`${label}: ${title}`}
    >
      <CardContent className="a3-automation-content">
        <header className="a3-automation-heading">
          <span className="a3-automation-state-icon" aria-hidden="true">
            {resolved ? (
              <ShieldCheck />
            ) : attention ? (
              <CircleSlash />
            ) : (
              <AlertTriangle />
            )}
          </span>
          <div className="a3-automation-title">
            <div className="a3-automation-eyebrow">
              <span>{label}</span>
              <time
                dateTime={
                  resolved
                    ? (incident.resolvedAt ?? incident.lastObservedAt)
                    : incident.firstObservedAt
                }
              >
                {formatClockTime(
                  resolved
                    ? (incident.resolvedAt ?? incident.lastObservedAt)
                    : incident.firstObservedAt,
                )}
              </time>
            </div>
            <h2>{title}</h2>
          </div>
          <Badge tone={resolved ? "success" : "warning"}>{status}</Badge>
        </header>

        <p className="a3-automation-summary">{summary}</p>

        <div className="a3-automation-action">
          <span className="a3-automation-action-icon" aria-hidden="true">
            {resolved ? <Check /> : attention ? <AlertOctagon /> : <Sparkles />}
          </span>
          <div>
            <strong>{action.title}</strong>
            <p>{action.detail}</p>
            {fixable ? (
              <div className="a3-automation-fix">
                <Button
                  type="button"
                  className="a3-action a3-action-primary"
                  disabled={fixing}
                  onClick={() => {
                    setFixing(true);
                    void onFix!(incident).finally(() => setFixing(false));
                  }}
                >
                  <Wrench aria-hidden="true" />
                  {fixing ? "Starting…" : "Fix it"}
                </Button>
                <span>It finds the cause, tests a fix and releases it.</span>
              </div>
            ) : null}
          </div>
        </div>

        <dl className="a3-automation-facts">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>

      <Accordion className="a3-automation-details">
        <AccordionItem value="details">
          <AccordionTrigger>
            <span className="a3-automation-disclosure">
              <ListChecks /> {resolved ? "Recovery evidence" : "Alert details"}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <dl>
              {details.map((detail) => (
                <div key={detail.label}>
                  <dt>{detail.label}</dt>
                  <dd>{detail.value}</dd>
                </div>
              ))}
            </dl>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

function startIncidentAction(incident: IncidentView): {
  title: string;
  detail: string;
} {
  if (incident.status === "detected" || incident.status === "queued") {
    return {
      title: "The app agent is queued to start",
      detail:
        "It will investigate this alert fire as soon as its repair run starts.",
    };
  }
  return {
    title: "The app agent started on it",
    detail:
      "It can inspect, change, and re-release this app. App data, secrets, and access stay outside this repair.",
  };
}

function terminalIncidentStatus(status: IncidentView["status"]): string {
  if (status === "cancelled") return "Stopped";
  if (status === "failed") return "Not fixed";
  return "Needs you";
}

function terminalIncidentAction(status: IncidentView["status"]): string {
  if (status === "failed") return "The app agent couldn't verify a fix";
  if (status === "cancelled") return "The fix was stopped";
  return "The app agent didn't start a fix";
}

/** What raised the card: an app alert, someone's report or another signal. */
function incidentLabel(source: IncidentView["source"]): string {
  if (source === "agent_feed") return "Alert";
  if (source === "user_report") return "Problem report";
  return "App problem";
}

function recoveryTitle(title: string): string {
  const recovered = title
    .replace(/\bfailed\b/gi, "recovered")
    .replace(/\bfailures?\b/gi, "recovery")
    .replace(/\berrors?\b$/i, "recovered");
  return recovered === title ? `${title} recovered` : recovered;
}

function recoveryDuration(startValue: string, endValue: string | null): string {
  if (!endValue) return "Verified";
  const seconds = Math.max(
    0,
    Math.round(
      (new Date(endValue).getTime() - new Date(startValue).getTime()) / 1_000,
    ),
  );
  if (!Number.isFinite(seconds)) return "Verified";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}m${remainingSeconds ? ` ${remainingSeconds}s` : ""}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h${remainingMinutes ? ` ${remainingMinutes}m` : ""}`;
}

function retainedEvidenceSummary(evidence: readonly string[]): string {
  if (evidence.length === 0) return "Verified";
  return `${evidence.length} retained ${evidence.length === 1 ? "record" : "records"}`;
}

function retainedEvidenceKinds(evidence: readonly string[]): string {
  const kinds = [
    ...new Set(
      evidence.map((value) => titleCase(value.split(":")[0] ?? "record")),
    ),
  ];
  return kinds.length > 0 ? kinds.join(", ") : "Platform verification";
}

function sourceLabel(source: IncidentView["source"]): string {
  const labels: Record<IncidentView["source"], string> = {
    health: "Health monitor",
    verification: "Release verification",
    runtime: "App runtime",
    function: "App function",
    job: "Background job",
    agent_feed: "Alert rule",
    user_report: "User report",
  };
  return labels[source];
}

function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatClockTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function transcriptViewport(frame: HTMLDivElement | null): HTMLElement | null {
  return (
    frame?.querySelector<HTMLElement>("[data-slot='scroll-area-viewport']") ??
    null
  );
}

function ToolGroup({
  entries,
  runs,
  onRetry,
}: {
  entries: readonly ToolTimelineEntry[];
  runs: ConversationSnapshot["runs"];
  onRetry?: (runId: string) => void;
}) {
  const calls = entries.map(({ value }) => value);
  const latestRoot = [...runs].reverse().find((run) => run.parentRunId === null);
  let callRoot = runs.find((run) => run.id === calls.at(-1)?.runId);
  const visited = new Set<string>();
  while (callRoot?.parentRunId && !visited.has(callRoot.id)) {
    visited.add(callRoot.id);
    callRoot = runs.find((run) => run.id === callRoot?.parentRunId);
  }
  const retryRunId = latestRoot?.id === callRoot?.id && latestRoot?.status === "failed"
    && latestRoot.checkpointVersion > 0 ? latestRoot.id : null;
  const identities = new Map(
    entries.flatMap((entry) =>
      entry.uiMessageId && entry.uiPartId && entry.uiPartState
        ? [
            [
              entry.value.id,
              {
                messageId: entry.uiMessageId,
                partId: entry.uiPartId,
                state: entry.uiPartState,
              },
            ] as const,
          ]
        : [],
    ),
  );
  return (
    <ToolActivityCard
      calls={calls}
      active={toolActivityIsLive(calls, runs)}
      {...(onRetry && retryRunId ? { onRetry: () => onRetry(retryRunId) } : {})}
      {...(identities.size > 0 ? { uiPartIdentities: identities } : {})}
    />
  );
}

function groupToolEntries(entries: TimelineEntry[]): TimelineGroup[] {
  const groups: TimelineGroup[] = [];
  for (const entry of entries) {
    const previous = groups.at(-1);
    if (entry.kind === "tool" && previous?.kind === "tools") {
      previous.entries.push(entry);
    } else if (entry.kind === "tool") {
      groups.push({ kind: "tools", entries: [entry] });
    } else {
      groups.push(entry);
    }
  }
  return groups;
}

function TranscriptMessage({
  viewerUserId,
  message,
  focused,
  readOnly,
  onRetry,
  streaming,
  canRegenerate,
  automaticRepair,
  automaticFailures,
}: {
  viewerUserId?: string | undefined;
  message: ConversationSnapshot["messages"][number];
  focused: boolean;
  readOnly: boolean;
  onRetry: (messageId: string) => boolean | Promise<boolean>;
  streaming: boolean;
  canRegenerate: boolean;
  automaticRepair: boolean;
  automaticFailures?: AutomaticFailureContext | undefined;
}) {
  const self = Boolean(message.actor && message.actor.userId === viewerUserId);
  const automaticFailure = message.role === "system" ? knownAutomaticFailure(message.automaticFailure) : null;
  if (automaticFailure) {
    return (
      <AutomaticFailureCard
        failure={automaticFailure}
        messageId={message.id}
        focused={focused}
        readOnly={readOnly}
        context={automaticFailures}
        formatTime={formatClockTime}
      />
    );
  }
  const appEvent = message.role === "system" ? knownAppEvent(message.appEvent) : null;
  if (appEvent) {
    return (
      <AppEventRow
        event={appEvent}
        actor={message.actor}
        self={self}
        messageId={message.id}
        focused={focused}
        timeLabel={formatClockTime(appEvent.occurredAt)}
      />
    );
  }
  if (message.role === "system") {
    const event = automationMessage(message.content);
    if (event && !message.actor) return <AutomationMessageCard event={event} messageId={message.id} focused={focused} />;
    return (
      <Alert
        id={`message-${message.id}`}
        data-message-id={message.id}
        data-search-focus={focused || undefined}
        className="a3-system-turn"
      >
        <Check aria-hidden="true" />
        {!self ? <AlertTitle>{message.actor ? <>
          {message.actor.avatarUrl ? <img src={message.actor.avatarUrl} alt="" width={24} height={24} /> : null}
          {message.actor.name || "Collaborator"}
        </> : agentBrand().productName}</AlertTitle> : null}
        <AlertDescription>
          <Markdown>{message.content}</Markdown>
        </AlertDescription>
      </Alert>
    );
  }
  const contextSummary = conversationContextSummary(message);
  if (contextSummary !== null) {
    return <ContextSummaryNote summary={contextSummary} messageId={message.id} focused={focused} />;
  }
  const user = message.role === "user";
  return (
    <OpenCloudMessage
      id={`message-${message.id}`}
      data-message-id={message.id}
      data-search-focus={focused || undefined}
      align={user ? "end" : "start"}
    >
      <MessageContent className="a3-turn-message-content" data-sender={user ? message.actor && !self ? "other" : "self" : undefined}>
        {!user && automaticRepair ? (
          <MessageHeader className="a3-repair-update-meta">
            <strong>App agent</strong>
            <span aria-hidden="true">·</span>
            <span>Automatic repair</span>
            <time dateTime={message.createdAt}>
              {formatClockTime(message.createdAt)}
            </time>
          </MessageHeader>
        ) : null}
        <Bubble
          className="a3-turn-bubble"
          align={user ? "end" : "start"}
          variant={user ? "tinted" : "ghost"}
        >
          <BubbleContent
            className={
              user
                ? "a3-user-turn"
                : `a3-agent-turn${automaticRepair ? " a3-repair-update" : ""}`
            }
          >
            {user && message.actor && !self ? <MessageHeader className="a3-sender-label">
              {message.actor.avatarUrl ? <img src={message.actor.avatarUrl} alt="" width={24} height={24} /> : null}
              <strong>{message.actor.name || "Collaborator"}</strong>
            </MessageHeader> : null}
            <Markdown className="a3-turn-copy">{message.content}</Markdown>
            <ChatAttachments files={message.attachments ?? []} deletedIds={message.deletedAttachmentIds} path={`/v1/conversations/${message.threadId}`} readOnly={readOnly}/>
          </BubbleContent>
        </Bubble>
        {!readOnly && !streaming ? (
          <MessageActions
            content={message.content}
            user={user}
            {...(!user && canRegenerate
              ? { onRetry: () => onRetry(message.id) }
              : {})}
          />
        ) : null}
      </MessageContent>
    </OpenCloudMessage>
  );
}

export async function copyUserMessage(
  content: string,
  clipboard: Pick<Clipboard, "writeText"> | undefined = globalThis.navigator
    ?.clipboard,
): Promise<boolean> {
  if (!clipboard) return false;
  try {
    await clipboard.writeText(content);
    return true;
  } catch {
    return false;
  }
}

export function conversationStartLabel(
  createdAt: string,
  now = new Date(),
  locale?: string,
): string | null {
  const started = new Date(createdAt);
  const ageMs = now.getTime() - started.getTime();
  if (!Number.isFinite(started.getTime()) || ageMs <= 60 * 60 * 1_000) {
    return null;
  }

  const time = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(started);
  if (sameLocalDay(started, now)) return `Today at ${time}`;

  const recentBoundary = new Date(now);
  recentBoundary.setHours(0, 0, 0, 0);
  recentBoundary.setDate(recentBoundary.getDate() - 6);
  if (started >= recentBoundary) {
    const weekday = new Intl.DateTimeFormat(locale, {
      weekday: "long",
    }).format(started);
    return `${weekday} at ${time}`;
  }

  const date = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(started);
  return `${date} at ${time}`;
}

function sameLocalDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

/** What paid for one finished run, retained by the service with the run. */
/** Failures a user fixes by connecting (or choosing) their own AI account. */
export const CONNECT_AI_FAILURE_CODES: ReadonlySet<string> = new Set([
  "ai_capacity_unavailable",
  "provider_connection_required",
  "provider_connection_not_ready",
]);

export function PendingMessage({
  message,
  onRetry,
  onAllowProvider,
  onConnectProvider,
  personalAiAccounts = true,
}: {
  message: PendingConversationMessage;
  onRetry: () => void;
  /** Grants this app chat the missing personal-connection permission, then resends. */
  onAllowProvider?: (() => void) | undefined;
  /** Opens Agent settings when no AI account can run the work. */
  onConnectProvider?: (() => void) | undefined;
  /** False where only organisation admins connect AI accounts, so settings only point to them. */
  personalAiAccounts?: boolean;
}) {
  return (
    <div className="a3-pending-turn" data-delivery={message.delivery}>
      <OpenCloudMessage align="end">
        <MessageContent className="a3-turn-message-content">
          <Bubble align="end" variant="tinted">
            <BubbleContent className="a3-user-turn">
              <Markdown className="a3-turn-copy">{message.content}</Markdown>
              <ChatAttachments files={message.attachments ?? []} readOnly/>
            </BubbleContent>
          </Bubble>
          <MessageActions content={message.content} user />
        </MessageContent>
      </OpenCloudMessage>
      {message.delivery === "failed" ? (
        <div className="a3-message-delivery" role="alert">
          <span>Message not sent.</span>
          {onAllowProvider &&
          message.failureCode === "provider_delegation_required" ? (
            <Button variant="outline" onClick={onAllowProvider}>
              Allow this app to use my connection and send
            </Button>
          ) : null}
          {onConnectProvider &&
          CONNECT_AI_FAILURE_CODES.has(message.failureCode ?? "") ? (
            <Button variant="outline" onClick={onConnectProvider}>
              {personalAiAccounts ? "Connect your AI" : "Open AI settings"}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onRetry}>
            <RotateCcw aria-hidden="true" /> Retry
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function StreamingMessage({ text }: { text: string }) {
  return (
    <OpenCloudMessage align="start" data-streaming="true">
      <MessageContent className="a3-turn-message-content">
        <Bubble className="a3-turn-bubble" align="start" variant="ghost">
          <BubbleContent className="a3-agent-turn a3-streaming-turn">
            <Markdown className="a3-turn-copy">{text}</Markdown>
          </BubbleContent>
        </Bubble>
        <div className="a3-message-actions" aria-hidden="true" />
      </MessageContent>
    </OpenCloudMessage>
  );
}

export function MessageActions({
  content,
  user,
  onRetry,
}: {
  content: string;
  user: boolean;
  onRetry?: () => boolean | Promise<boolean>;
}) {
  const [copied, setCopied] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retried, setRetried] = useState(false);
  const copiedTimer = useRef<number | null>(null);
  const retriedTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copiedTimer.current !== null) {
        window.clearTimeout(copiedTimer.current);
      }
      if (retriedTimer.current !== null) {
        window.clearTimeout(retriedTimer.current);
      }
    },
    [],
  );

  async function copyMessage() {
    if (await copyUserMessage(content)) {
      setCopied(true);
      if (copiedTimer.current !== null) {
        window.clearTimeout(copiedTimer.current);
      }
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1_600);
    } else {
      setCopied(false);
    }
  }

  async function retryMessage() {
    if (!onRetry || retrying) return;
    setRetrying(true);
    try {
      const accepted = await onRetry();
      if (accepted === false) return;
      setRetried(true);
      if (retriedTimer.current !== null) {
        window.clearTimeout(retriedTimer.current);
      }
      retriedTimer.current = window.setTimeout(() => setRetried(false), 1_600);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div className="a3-message-actions" data-align={user ? "end" : "start"}>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label={
          copied
            ? "Message copied"
            : user
              ? "Copy your message"
              : "Copy agent message"
        }
        title={copied ? "Copied" : "Copy message"}
        data-success={copied || undefined}
        onClick={() => void copyMessage()}
      >
        {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </Button>
      {onRetry ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={
            retried
              ? "Response regeneration started"
              : retrying
                ? "Regenerating response"
                : "Regenerate agent response"
          }
          title={retried ? "Regeneration started" : "Regenerate response"}
          data-success={retried || undefined}
          disabled={retrying}
          onClick={() => void retryMessage()}
        >
          {retried ? (
            <Check aria-hidden="true" />
          ) : (
            <RotateCcw aria-hidden="true" />
          )}
        </Button>
      ) : null}
    </div>
  );
}

function OutcomeCard({
  outcome,
  run,
  releases,
  appName,
  repeatSummary,
  appUrl,
  onLoadRelease,
  readOnly,
}: {
  outcome: ConversationOutcome;
  run: ConversationSnapshot["runs"][number] | null;
  releases: readonly AgentRelease[];
  appName: string | null;
  repeatSummary: boolean;
  appUrl: string | null;
  onLoadRelease: (releaseId: string) => Promise<AgentRelease>;
  readOnly: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const knownRelease = outcome.release?.deploymentId
    ? (releases.find(
        (release) => release.id === outcome.release?.deploymentId,
      ) ?? null)
    : null;
  const [loadedRelease, setLoadedRelease] = useState<AgentRelease | null>(
    knownRelease,
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const evidenceAnchor = useRef<HTMLElement>(null);
  const detail = loadedRelease ?? knownRelease;
  const reviews = mergeEvidence(
    detail?.reviews ?? [],
    outcome.release?.reviews ?? [],
    (review) => `${review.area}:${review.label}`,
  );
  const gates = mergeEvidence(
    detail?.gates ?? [],
    outcome.release?.gates ?? [],
    (gate) => gate.id,
  );

  useEffect(() => {
    if (!expanded) return;
    const frame = requestAnimationFrame(() => {
      const evidence = evidenceAnchor.current;
      const transcriptRoot = evidence?.closest<HTMLElement>(".a3-transcript");
      const transcript = transcriptRoot?.querySelector<HTMLElement>(
        "[data-slot='scroll-area-viewport']",
      );
      if (!evidence || !transcript) return;
      const evidenceBox = evidence.getBoundingClientRect();
      const transcriptBox = transcript.getBoundingClientRect();
      transcript.scrollTop += evidenceBox.top - transcriptBox.top - 16;
    });
    return () => cancelAnimationFrame(frame);
  }, [expanded, loadedRelease]);

  async function toggleRelease() {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    const deploymentId = outcome.release?.deploymentId;
    if (!deploymentId || detail || readOnly) return;
    setLoading(true);
    setLoadError(false);
    try {
      setLoadedRelease(await onLoadRelease(deploymentId));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  if (outcome.status === "released" && outcome.release?.deploymentId) {
    const needsAttention = outcome.release.status === "failed";
    return (
      <Card
        className="a3-settled a3-release-card"
        data-release-status={needsAttention ? "attention" : "published"}
        data-deployment-id={outcome.release.deploymentId}
      >
        <header>
          <span className="a3-settled-icon" aria-hidden="true">
            {needsAttention ? <AlertOctagon /> : <Check />}
          </span>
          <div>
            <p className="a3-eyebrow">
              Published · <time dateTime={outcome.occurredAt}>{formatDateTime(outcome.occurredAt)}</time>
            </p>
            <h3>
              {appName ?? "Your app"} · {releaseHeadline(outcome.release.deploymentId, releases) === "Your app is updated" ? "Update published" : "Ready to open"}
            </h3>
          </div>
        </header>
        {!repeatSummary ? <Markdown>{outcome.summary}</Markdown> : null}
        {needsAttention ? <p className="a3-release-note" role="status">The app was published, but recorded checks need attention.</p> : null}
        {detail?.capabilities.length ? <ul className="a3-release-features">{detail.capabilities.slice(0, 4).map((capability) =>
          <li key={capability}><Check aria-hidden="true" /><span>{capability}</span></li>)}</ul> : null}
        <div className="a3-actions">
          {appUrl ? (
            <Button
              render={<a href={appUrl} target="_blank" rel="noreferrer" />}
              className="a3-action a3-action-primary"
            >
              <ExternalLink aria-hidden="true" />
              Open app
            </Button>
          ) : null}
          <Button
            type="button"
            className="a3-action a3-action-secondary"
            variant="outline"
            aria-expanded={expanded}
            onClick={() => void toggleRelease()}
          >
            <ChevronRight className="a3-disclosure" aria-hidden="true" />
            {expanded ? "Hide release details" : "Release details"}
          </Button>
        </div>
        {expanded ? (
          <div className="a3-inline-release" aria-live="polite">
            {loading ? <p>Loading release details…</p> : null}
            {detail ? <p className="a3-inline-release-note">Release {detail.version} · Published {formatDateTime(detail.activatedAt ?? outcome.occurredAt)}</p> : null}
            {detail?.capabilities.length ? (
              <section>
                <h4>Parts included</h4>
                <ul>
                  {detail.capabilities.map((capability) => (
                    <li key={capability}>{capability}</li>
                  ))}
                </ul>
              </section>
            ) : null}
            {loadError ? (
              <p className="a3-inline-release-note">
                Full release details are unavailable. The checks recorded in
                this conversation are shown below.
              </p>
            ) : null}
            <section ref={evidenceAnchor}>
              <h4>How this was checked</h4>
              <Evidence reviews={reviews} gates={gates} record />
            </section>
          </div>
        ) : null}
        {run?.status === "failed" || run?.status === "cancelled" ? <p className="a3-release-note" role="status">
          The app was published. The agent {run.status === "failed" ? "was interrupted" : "stopped"} before finishing the turn. You can continue the conversation below.
        </p> : null}
      </Card>
    );
  }
  if (
    outcome.status === "completed" &&
    !repeatSummary &&
    !/^the requested work finished\.?$/i.test(outcome.summary.trim())
  ) {
    return (
      <div className="a3-terminal-summary">
        <Markdown>{outcome.summary}</Markdown>
      </div>
    );
  }
  return null;
}

function mergeEvidence<T>(
  base: readonly T[],
  overlay: readonly T[],
  key: (value: T) => string,
): T[] {
  const merged = new Map(base.map((value) => [key(value), value]));
  for (const value of overlay) merged.set(key(value), value);
  return [...merged.values()];
}
